import { createEventHook, useDebounceFn } from '@vueuse/core';
import { computed, readonly, ref, watch, type ComputedRef } from 'vue';

import * as BridgeEvents from '@/common/bridge/modern-bridge/bridge-events';

import type { PleasedChat } from '@/pleased-sdk/PleasedChat/PleasedChat';
import { useAtomic } from '@/use/useAtomic';
import { PleasedClient } from '@/pleased-sdk/PleasedClient';
import type { PleasedChatHandler } from '@/pleased-sdk/PleasedChat/PleasedChat.types';
import { useBotUser } from '@/stores/useBotUser';

import type { MessageUtilities } from '../message/useMessageUtilities';
import { useMessageUtilities } from '../message/useMessageUtilities';
import { createChatMessage, createMediaMessage, createNotice, createTypingMessage } from '../message/lib';
import { MemberType, NoticeType } from '../message/enum';
import type { PleasedChatRequest } from '../cf-v1/usePleasedSupport.types';
import { SystemMessageConfig } from '@/common/api/models.pleased';
import { getChatById } from '@/common/api/api.pleased';
import { logger } from '@/common/api/logger';
import { parseErrorMessage, RequestLimitError } from '@/common/error';
import {
  captureEventException,
  ErrorTrackingFlow,
  ErrorTrackingEvent,
  setChatbotContext,
} from '@/common/error-tracking';
import { createChatSessionHostEvents } from './useChatSessionHostEvents';

const STATIC_ITEM_ID = {
  queuePosition: 'chat-queue-position',
  userNetwork: 'is-user-connected',
};

export enum PleasedChatEvent {
  STARTED = 'started',
  CLOSED = 'closed',
}

/** GET snapshot (token + time-since-triggers) before loading an existing chat for intervention. */
export async function mergeExistingChatLoadSnapshot(input: {
  existingChatId: number;
  firebaseToken: string;
  timeSinceTriggers: SystemMessageConfig[] | undefined;
}): Promise<{ firebaseToken: string; timeSinceTriggers: SystemMessageConfig[] | undefined }> {
  let { firebaseToken, timeSinceTriggers } = input;
  try {
    const snapshot = await getChatById(input.existingChatId);
    firebaseToken = snapshot.firebaseToken || firebaseToken;
    timeSinceTriggers = snapshot.timeSinceTriggers ?? timeSinceTriggers;
  } catch (err) {
    logger.error('[PLEASED_CHAT_CONTROLLER] getChatById failed, using request payload', {
      error: parseErrorMessage(err),
    });
  }
  return { firebaseToken, timeSinceTriggers };
}

export function usePleasedChat() {
  const conversation = useMessageUtilities();

  const chatId = ref<null | number>(null);
  const chat = computed(() => (chatId.value ? PleasedClient.get('chat', chatId.value) : null));
  const active = ref(false);
  let removeController: null | VoidFunction = null;
  const hostEvents = createChatSessionHostEvents();

  // Keep the loader's `get:is_chatting` value in sync with live (agent) chat state.
  watch(active, (isChatting) => BridgeEvents.postChattingState(isChatting));

  const chatHook = createEventHook<{ chat: PleasedChat; event: PleasedChatEvent }>();

  // TODO: update interface with new fields
  const [createChat, isCreatingChat, errorCreatingChat] = useAtomic(async (request: PleasedChatRequest['payload']) => {
    // Remove any existing controller as new chat is starting.
    removeController?.();
    const { user } = useBotUser();
    const chat = PleasedClient.chat({
      user,
      ...request,
    });
    const { clear: removeChatController } = setupChatController({ chat, conversation, hostEvents });
    removeController = removeChatController;
    logger.debug('[PLEASED_CHAT_CONTROLLER] Creating new chat', { chatId: chat.id });

    await chat.create().catch((err) => {
      logger.error('[PLEASED_CHAT_CONTROLLER] Error creating new chat', { chatId: chat.id, error: err?.message });
      if (!(err instanceof RequestLimitError)) {
        setChatbotContext({ flow: ErrorTrackingFlow.OpenChat });
        captureEventException(err, ErrorTrackingEvent.OpenChat);
      }
      // If there is an error creating the chat, we should remove the chat controller.
      removeChatController();
      removeController = null;
      throw err;
    });

    logger.debug('[PLEASED_CHAT_CONTROLLER] Chat created successfully', { chatId: chat.id });

    chatId.value = chat.id as number;
    active.value = true;

    chat.on('chat:closed', ({ reason }) => {
      hostEvents.handleClosed(reason);
      chatHook.trigger({ chat, event: PleasedChatEvent.CLOSED });
      active.value = false;

      removeChatController();
      removeController = null;
    });

    hostEvents.handleStarted();
    chatHook.trigger({ chat, event: PleasedChatEvent.STARTED });
    return chat;
  });

  /**
   * Loads an existing chat (for AI intervention or resuming a chat)
   * @param payload - Configuration object
   * @param payload.chatId - The chat ID
   * @param payload.firebaseToken - Firebase token for authentication
   * @param payload.timeSinceTriggers - System message triggers
   * @param payload.generateSessionId - If true, generates a new resumable session ID for chat resume
   * @param payload.resumeMessage - Optional message to send when resuming the chat
   * @param payload.fetchChatSnapshot - If true, GET /chat-widget/chat/:id first (intervention: token + timeSinceTriggers from BE)
   */
  const [loadExistingChat, isLoadingExistingChat, errorLoadingExistingChat] = useAtomic(
    async (payload: {
      chatId: number;
      firebaseToken: string;
      timeSinceTriggers?: SystemMessageConfig[];
      generateSessionId?: boolean;
      resumeMessage?: string;
      fetchChatSnapshot?: boolean;
    }) => {
      const {
        chatId: existingChatId,
        firebaseToken: passedFirebaseToken,
        timeSinceTriggers: passedTimeSinceTriggers,
        generateSessionId,
        resumeMessage,
        fetchChatSnapshot,
      } = payload;

      let firebaseToken = passedFirebaseToken;
      let timeSinceTriggers = passedTimeSinceTriggers;

      if (fetchChatSnapshot) {
        const merged = await mergeExistingChatLoadSnapshot({
          existingChatId,
          firebaseToken: passedFirebaseToken,
          timeSinceTriggers: passedTimeSinceTriggers,
        });
        firebaseToken = merged.firebaseToken;
        timeSinceTriggers = merged.timeSinceTriggers;
      }

      removeController?.();

      try {
        const { user } = useBotUser();
        const visitor = {
          id: user.id,
          displayName: user.displayName,
          email: user.email,
          phone: user.phone,
          foreignId: user.foreignId,
          isGuest: user.isGuest,
        };

        const loadedChat = await PleasedClient.loadExistingChat({
          chatId: existingChatId,
          visitor,
          firebaseToken,
          timeSinceTriggers,
          generateSessionId,
          skipFirebaseSetup: true,
        });

        const { clear: removeChatController } = setupChatController({ chat: loadedChat, conversation, hostEvents });
        removeController = removeChatController;

        await loadedChat.connectFirebase(firebaseToken);

        if (resumeMessage) {
          await loadedChat.sendResumeMessage(resumeMessage);
        }

        chatId.value = existingChatId;
        active.value = true;

        logger.debug('[PLEASED_CHAT_CONTROLLER] Chat loaded successfully', { chatId: loadedChat.id });

        loadedChat.on('chat:closed', ({ reason }) => {
          hostEvents.handleClosed(reason);
          chatHook.trigger({ chat: loadedChat, event: PleasedChatEvent.CLOSED });
          active.value = false;

          removeChatController();
          removeController = null;
        });

        hostEvents.handleStarted();
        chatHook.trigger({ chat: loadedChat, event: PleasedChatEvent.STARTED });
        return loadedChat;
      } catch (err) {
        logger.error('[PLEASED_CHAT_CONTROLLER] Error loading existing chat', {
          chatId: existingChatId,
          error: parseErrorMessage(err),
        });
        setChatbotContext({ flow: ErrorTrackingFlow.OpenChatLoad });
        captureEventException(err, ErrorTrackingEvent.OpenChatLoad);
        removeController?.();
        removeController = null;
        throw err;
      }
    }
  );

  const isChatSessionLoading: ComputedRef<boolean> = computed(() =>
    Boolean(isCreatingChat.value || isLoadingExistingChat.value)
  );

  function resetError() {
    errorCreatingChat.value = '';
    errorLoadingExistingChat.value = null;
  }

  function reset() {
    if (active.value) {
      logger.warn('You are resetting the chat before an active chat was closed, this can have side effects.', {
        chatId: chatId.value,
      });
    }
    resetError();
    hostEvents.dispose();
    removeController?.();
    conversation.clear({ __CLEARING_CONVERSATION: true });
    active.value = false;
    chatId.value = null;
  }

  return {
    chat,
    chatMessages: conversation.messages,
    createChat,
    loadExistingChat,
    isCreatingChat,
    isLoadingExistingChat,
    isChatSessionLoading,
    errorCreatingChat,
    errorLoadingExistingChat,
    chatId: readonly(chatId),
    onChat: chatHook.on,
    active: readonly(active),
    resetError,
    reset,
  };
}

function setupChatController({
  chat,
  conversation,
  hostEvents,
}: {
  chat: PleasedChat;
  conversation: MessageUtilities;
  hostEvents: ReturnType<typeof createChatSessionHostEvents>;
}) {
  const { addItems, deleteItem } = conversation;

  const onCustomerActivity = () => {
    hostEvents.handleUserActivity();
  };

  const onChatQueuePosition: PleasedChatHandler<'chat:queue:position'> = ({ position }) => {
    if (position === 'clear') return deleteItem(createNotice({ id: STATIC_ITEM_ID.queuePosition }));

    addItems(
      createNotice({
        id: STATIC_ITEM_ID.queuePosition,
        type: NoticeType.QUEUE_POSITION,
        icon: 'MdPeople',
        isSticky: true,
        message: `You are #${position} in the queue`,
      })
    );
    return;
  };

  const onChatMessage: PleasedChatHandler<'chat:message'> = ({ attachment, ...message }) => {
    // Do not add user selections back to the conversation.
    if (message.userSelection) return;
    const messageProps = {
      memberType: message.authorType === 'agent' ? MemberType.AGENT : MemberType.VISITOR,
      displayName: message.authorName,
      id: message.id,
      message: message.message,
      timestamp: message.timestamp,
    };
    const chatMessage = attachment
      ? createMediaMessage({
          ...messageProps,
          attachment: {
            contentType: attachment.contentType,
            mimeType: attachment.contentType,
            name: attachment.name,
            url: attachment.url,
            id: attachment.id,
          },
        })
      : createChatMessage(messageProps);
    addItems(chatMessage);
  };

  const onMemberParticipation: PleasedChatHandler<'chat:member:participation'> = (message) => {
    // Do not add notices for customer add/remove.
    if (message.member.type === 'customer') return;
    const messageType = message.type === 'member:join' ? NoticeType.MEMBER_JOINED : NoticeType.MEMBER_LEFT;
    addItems(
      createNotice({
        type: messageType,
        message: `${message.member.name} has ${message.type === 'member:join' ? 'joined' : 'left'} the chat`,
        timestamp: message.member.timestamp,
      })
    );
  };

  const onTyping: PleasedChatHandler<'user:typing'> = (evt) => {
    const typingNotice = createTypingMessage('agent-typing-message');
    Object.values(evt).forEach((e) => {
      if (!e.isAgent) return;
      if (e.isTyping) addItems(typingNotice);
      else deleteItem(typingNotice);
    });
  };

  const onClose = () => {
    addItems(createNotice({ message: 'The chat has been closed', type: NoticeType.CHAT_ENDED }));
    clear();
  };

  const onNetworkStateChange = useDebounceFn(() => {
    // The very first time this is triggered, this will be offline by default.
    // We need a better way to denote the user that the connection is wobbly.
    // currently, using debounce we mitigate the side effect.
    const connected = chat.isConnected || window.navigator.onLine;
    const userOfflineNotice = createNotice({
      id: STATIC_ITEM_ID.userNetwork,
      isSticky: true,
      type: NoticeType.NETWORK_ISSUES,
      message: 'Trying to connect... please check your internet connection',
    });
    if (!connected) return chat.isActive && addItems(userOfflineNotice);
    deleteItem(userOfflineNotice);
  }, 500);

  chat
    .on('chat:queue:position', onChatQueuePosition)
    .on('chat:member:participation', onMemberParticipation)
    .on('chat:message', onChatMessage)
    .on('user:typing', onTyping)
    .on('chat:network:connected', onNetworkStateChange)
    .on('chat:customer:activity', onCustomerActivity)
    .on('chat:closed', onClose);

  const clear = () => {
    chat
      .off('chat:queue:position', onChatQueuePosition)
      .off('chat:member:participation', onMemberParticipation)
      .off('chat:message', onChatMessage)
      .off('user:typing', onTyping)
      .off('chat:network:connected', onNetworkStateChange)
      .off('chat:customer:activity', onCustomerActivity)
      .off('chat:closed', onClose);
  };

  return { clear };
}

export function usePleasedChatController({ chat }: { chat: PleasedChat }) {
  const api = useMessageUtilities();
  const { addItems, deleteItem } = api;

  chat.on('chat:queue:position', ({ position }) => {
    if (position === 'clear') return deleteItem(createNotice({ id: STATIC_ITEM_ID.queuePosition }));

    addItems(
      createNotice({
        id: STATIC_ITEM_ID.queuePosition,
        type: NoticeType.QUEUE_POSITION,
        isSticky: true,
        message: `You are #${position} in the queue`,
      })
    );
    return;
  });

  chat.on('chat:message', ({ attachment, ...message }) => {
    const messageProps = {
      memberType: message.authorType === 'agent' ? MemberType.AGENT : MemberType.VISITOR,
      displayName: message.authorName,
      id: message.id,
      message: message.message,
      timestamp: message.timestamp,
    };
    const chatMessage = attachment
      ? createMediaMessage({
          ...messageProps,
          attachment: {
            contentType: attachment.contentType,
            mimeType: attachment.contentType,
            name: attachment.name,
            url: attachment.url,
            id: attachment.id,
          },
        })
      : createChatMessage(messageProps);
    addItems(chatMessage);
  });

  chat.on('chat:member:participation', (message) => {
    // Do not add notices for customer add/remove.
    if (message.member.type === 'customer') return;
    const messageType = message.type === 'member:join' ? NoticeType.MEMBER_JOINED : NoticeType.MEMBER_LEFT;
    addItems(
      createNotice({
        type: messageType,
        message: `${message.member.name} has ${message.type === 'member:join' ? 'joined' : 'left'} the chat`,
      })
    );
  });

  chat.on('user:typing', (evt) => {
    const typingNotice = createTypingMessage('agent-typing-message');
    Object.values(evt).forEach((e) => {
      if (!e.isAgent) return;
      if (e.isTyping) addItems(typingNotice);
      else deleteItem(typingNotice);
    });
  });

  chat.on('chat:closed', () => {
    addItems(createNotice({ message: 'The chat has been closed', type: NoticeType.CHAT_ENDED }));
  });

  const onNetworkStateChange = useDebounceFn(() => {
    // The very first time this is triggered, this will be offline by default.
    // We need a better way to denote the user that the connection is wobbly.
    // currently, using debounce we mitigate the side effect.
    const connected = chat.isConnected || window.navigator.onLine;
    const userOfflineNotice = createNotice({
      id: STATIC_ITEM_ID.userNetwork,
      isSticky: true,
      type: NoticeType.NETWORK_ISSUES,
      message: 'Trying to connect... please check your internet connection',
    });
    if (!connected) return chat.isActive && addItems(userOfflineNotice);
    deleteItem(userOfflineNotice);
  }, 500);

  chat.on('chat:network:connected', onNetworkStateChange);

  return {
    ...api,
  };
}
